import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Heart, CreditCard } from 'lucide-react';

const TIP_TEXT = {
  en: { title: "Add a Tip?", subtitle: "Show your appreciation to our team", no_tip: "No tip", custom: "Custom", pay: "Pay", total: "Total with tip" },
  fr: { title: "Ajouter un pourboire ?", subtitle: "Montrez votre appreciation a notre equipe", no_tip: "Sans pourboire", custom: "Autre", pay: "Payer", total: "Total avec pourboire" },
  th: { title: "ให้ทิปไหม?", subtitle: "แสดงความขอบคุณต่อทีมงานของเรา", no_tip: "ไม่ให้ทิป", custom: "กำหนดเอง", pay: "ชำระเงิน", total: "รวมทิป" },
  ru: { title: "Оставить чаевые?", subtitle: "Покажите признательность нашей команде", no_tip: "Без чаевых", custom: "Другое", pay: "Оплатить", total: "Итого с чаевыми" },
  de: { title: "Trinkgeld geben?", subtitle: "Zeigen Sie Ihrem Team Wertschatzung", no_tip: "Kein Trinkgeld", custom: "Andere", pay: "Bezahlen", total: "Gesamt mit Trinkgeld" },
  es: { title: "Dejar propina?", subtitle: "Muestre su agradecimiento a nuestro equipo", no_tip: "Sin propina", custom: "Otro", pay: "Pagar", total: "Total con propina" },
  ar: { title: "إضافة إكرامية؟", subtitle: "أظهر تقديرك لفريقنا", no_tip: "بدون إكرامية", custom: "مخصص", pay: "ادفع", total: "المجموع مع الإكرامية" },
  zh: { title: "给小费？", subtitle: "感谢我们团队的服务", no_tip: "不给小费", custom: "自定义", pay: "支付", total: "含小费总计" },
};

const TIP_PERCENTAGES = [10, 15, 20];

const TipSelector = ({ isOpen, onClose, onConfirm, billAmount }) => {
  const { language } = useLanguage();
  const [selectedTip, setSelectedTip] = useState(null);
  const [customAmount, setCustomAmount] = useState('');

  const txt = TIP_TEXT[language] || TIP_TEXT.en;

  const getTipAmount = () => {
    if (selectedTip === 0) return 0;
    if (selectedTip === 'custom') return parseFloat(customAmount) || 0;
    if (selectedTip) return Math.round(billAmount * selectedTip / 100);
    return 0;
  };

  const tipAmount = getTipAmount();
  const totalWithTip = billAmount + tipAmount;

  const formatPrice = (price) => `฿${Math.round(price).toLocaleString()}`;

  const handleConfirm = () => {
    onConfirm(tipAmount);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm mx-auto bg-[#0D0D0D] border border-[#333333] rounded-3xl p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 text-center">
          <div className="w-14 h-14 bg-gradient-to-br from-[#D4A853] to-[#E16833] rounded-full flex items-center justify-center mx-auto mb-3">
            <Heart className="w-7 h-7 text-white" />
          </div>
          <DialogTitle className="text-xl font-bold text-white">{txt.title}</DialogTitle>
          <p className="text-sm text-[#A0A0A0] mt-1">{txt.subtitle}</p>
        </DialogHeader>

        <div className="px-6 pb-2">
          {/* Bill amount display */}
          <div className="bg-[#1A1A1A] rounded-2xl p-3 mb-4 text-center">
            <p className="text-sm text-[#A0A0A0]">{txt.total}</p>
            <p className="text-2xl font-bold text-[#D4A853]">{formatPrice(totalWithTip)}</p>
            {tipAmount > 0 && (
              <p className="text-xs text-[#A0A0A0] mt-1">
                {formatPrice(billAmount)} + {formatPrice(tipAmount)} tip
              </p>
            )}
          </div>

          {/* Tip percentage buttons */}
          <div className="grid grid-cols-3 gap-3 mb-3">
            {TIP_PERCENTAGES.map(pct => {
              const amount = Math.round(billAmount * pct / 100);
              const isActive = selectedTip === pct;
              return (
                <button
                  key={pct}
                  onClick={() => { setSelectedTip(pct); setCustomAmount(''); }}
                  className={`rounded-2xl p-3 border-2 transition-all ${
                    isActive
                      ? 'border-[#D4A853] bg-[#D4A853]/15'
                      : 'border-[#333333] bg-[#1A1A1A] hover:border-[#555555]'
                  }`}
                  data-testid={`tip-${pct}`}
                >
                  <span className={`text-xl font-bold block ${isActive ? 'text-[#D4A853]' : 'text-white'}`}>
                    {pct}%
                  </span>
                  <span className={`text-xs block mt-1 ${isActive ? 'text-[#D4A853]/80' : 'text-[#A0A0A0]'}`}>
                    {formatPrice(amount)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* No tip button */}
          <button
            onClick={() => { setSelectedTip(0); setCustomAmount(''); }}
            className={`w-full rounded-2xl p-3 border-2 transition-all mb-4 ${
              selectedTip === 0
                ? 'border-[#D4A853] bg-[#D4A853]/15 text-[#D4A853]'
                : 'border-[#333333] bg-[#1A1A1A] hover:border-[#555555] text-[#A0A0A0]'
            }`}
            data-testid="tip-none"
          >
            <span className="font-semibold text-sm">{txt.no_tip}</span>
          </button>
        </div>

        {/* Confirm button */}
        <div className="p-6 pt-0">
          <Button
            onClick={handleConfirm}
            disabled={selectedTip === null}
            className="w-full h-14 rounded-full text-base font-bold bg-gradient-to-r from-[#D4A853] to-[#E16833] hover:opacity-90 text-black disabled:opacity-30"
            data-testid="confirm-tip-btn"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            {txt.pay} {formatPrice(totalWithTip)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TipSelector;
